import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Button, Space, Select, Tag, List, Typography, Row, Col, message, Tabs, Statistic, Table, Empty, Badge,
} from 'antd';
import {
  SendOutlined, SyncOutlined, FileTextOutlined, TwitterOutlined,
} from '@ant-design/icons';
import { apiGet, apiPost } from '../services/api';

const { Title, Text } = Typography;

// 商务风格配色
const COLORS = {
  primary: '#1677ff',
  secondary: '#722ed1',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  // 兼容旧代码的别名
  cyan: '#1677ff',
  purple: '#722ed1',
  pink: '#ff4d4f',
  green: '#52c41a',
  orange: '#faad14',
  yellow: '#faad14',
  darkBg: '#f5f5f5',
  cardBg: '#ffffff',
  borderDark: '#f0f0f0',
};

const SOURCE_TYPE_OPTIONS = [{ code: 'blockbeats', label: 'BlockBeats资讯' }, { code: 'twitter', label: 'Twitter推文' }];
const STATUS_OPTIONS = [
  { code: 'pending', label: '待处理', color: '#6b7280' },
  { code: 'processing', label: '处理中', color: COLORS.warning },
  { code: 'completed', label: '已完成', color: COLORS.success },
  { code: 'failed', label: '失败', color: COLORS.error },
];

interface ContentTask {
  id: number; sourceType: string; sourceId: number; contentType: string; keywords: string[]; aiSummary: string;
  priority: number; status: string; assignedCount: number; maxAssign: number; expireTime: string; createTime: string;
}
interface ContentDetail {
  id: number; taskId: number; botId: number; botName?: string; transformStyle: string; transformedContent: string;
  personalComment: string; scheduledTime: string; actualPublishTime: string; platformPostId: string; status: string;
}

// 简洁页面标题组件
const PageHeader: React.FC<{ icon: React.ReactNode; title: string; subtitle?: string; color?: string }> = ({ icon, title, subtitle, color = COLORS.primary }) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
      <div style={{
        width: 48, height: 48,
        background: `${color}10`,
        borderRadius: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 24, color: color,
      }}>{icon}</div>
      <div>
        <Title level={2} style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>{title}</Title>
        {subtitle && <Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 14 }}>{subtitle}</Text>}
      </div>
    </div>
    <div style={{
      width: 120, height: 3, marginLeft: 60,
      background: `linear-gradient(90deg, ${color}, ${color}66, transparent)`,
      borderRadius: 2,
    }} />
  </div>
);

// 简洁卡片组件
const SimpleCard: React.FC<{ title?: React.ReactNode; extra?: React.ReactNode; children: React.ReactNode; style?: React.CSSProperties; accentColor?: string }> = ({ title, extra, children, style, accentColor = COLORS.primary }) => (
  <Card
    title={title ? <Text style={{ fontWeight: 600, fontSize: 16 }}>{title}</Text> : undefined}
    extra={extra}
    style={{
      background: COLORS.cardBg,
      border: `1px solid ${COLORS.borderDark}`,
      borderRadius: 12,
      overflow: 'hidden',
      ...style,
    }}
    styles={{
      header: title ? {
        borderBottom: `1px solid ${COLORS.borderDark}`,
        background: `${accentColor}08`,
      } : undefined,
      body: { padding: 24 },
    }}
  >
    {children}
  </Card>
);

// 简洁统计卡片
const StatCard: React.FC<{ title: string; value: number; color: string }> = ({ title, value, color }) => (
  <Card style={{
    background: COLORS.cardBg,
    border: `1px solid ${COLORS.borderDark}`,
    borderRadius: 12,
  }}>
    <Statistic
      title={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>{title}</Text>}
      value={value}
      valueStyle={{ color: color, fontWeight: 700 }}
    />
  </Card>
);

const ContentDistribution: React.FC = () => {
  const [tasks, setTasks] = useState<ContentTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterSource, setFilterSource] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('pending');
  const [selectedTask, setSelectedTask] = useState<ContentTask | null>(null);
  const [details, setDetails] = useState<ContentDetail[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [stats, setStats] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<string>('pool');
  const [authors, setAuthors] = useState<any[]>([]);
  const [authorsLoading, setAuthorsLoading] = useState(false);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterSource) params.append('sourceType', filterSource);
      if (filterStatus) params.append('status', filterStatus);
      const data = await apiGet<any>(`/api/content/pool?${params.toString()}`);
      setTasks(Array.isArray(data) ? data : (data?.data || []));
    } catch (e: any) { message.error('加载失败: ' + e.message); setTasks([]); }
    finally { setLoading(false); }
  }, [filterSource, filterStatus]);

  const fetchDetails = useCallback(async (taskId: number) => {
    setDetailsLoading(true);
    try {
      const data = await apiGet<any>(`/api/content/task/${taskId}/details`);
      setDetails(Array.isArray(data) ? data : (data?.data || []));
    } catch (e) { setDetails([]); }
    finally { setDetailsLoading(false); }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const data = await apiGet<any>(`/api/content/stats`);
      setStats(data?.data || data);
    } catch (e) { console.error('加载统计失败', e); }
  }, []);

  const fetchAuthors = useCallback(async () => {
    setAuthorsLoading(true);
    try {
      const data = await apiGet<any>(`/api/twitter-author`);
      setAuthors(Array.isArray(data) ? data : (data?.data || []));
    } catch (e) { setAuthors([]); }
    finally { setAuthorsLoading(false); }
  }, []);

  useEffect(() => { fetchTasks(); fetchStats(); }, [fetchTasks, fetchStats]);
  useEffect(() => { if (selectedTask) fetchDetails(selectedTask.id); }, [selectedTask, fetchDetails]);
  useEffect(() => { if (activeTab === 'twitter-author') fetchAuthors(); }, [activeTab, fetchAuthors]);

  const handleScan = async (sourceType: string) => {
    setScanning(true);
    try {
      const data = await apiPost<any>(`/api/content/pool/scan`, { sourceType, limit: 50 });
      message.success(`扫描完成: 新增 ${data.scanned || 0} 条内容`);
      fetchTasks(); fetchStats();
    } catch (e: any) { message.error('扫描失败: ' + e.message); }
    finally { setScanning(false); }
  };

  const handleAssign = async (taskId: number) => {
    setAssigning(true);
    try {
      const data = await apiPost<any>(`/api/content/task/${taskId}/assign`, {});
      if (data.botId) { message.success(`已分配给机器人: ${data.botName || data.botId}`); fetchTasks(); if (selectedTask?.id === taskId) fetchDetails(taskId); }
    } catch (e: any) { message.error('分配失败: ' + e.message); }
    finally { setAssigning(false); }
  };

  const handlePublish = async (detailId: number) => {
    try {
      const data = await apiPost<any>(`/api/content/detail/${detailId}/publish`, {});
      if (data.success) { message.success('发布成功'); if (selectedTask) fetchDetails(selectedTask.id); fetchTasks(); }
    } catch (e: any) { message.error('发布失败: ' + e.message); }
  };

  const getStatusTag = (status: string) => {
    const opt = STATUS_OPTIONS.find(o => o.code === status);
    return <Tag style={{ background: `${opt?.color}15`, border: `1px solid ${opt?.color}33`, color: opt?.color || '#6b7280' }}>{opt?.label || status}</Tag>;
  };

  const authorColumns = [
    { title: 'Twitter Handle', dataIndex: 'twitterHandle', key: 'twitterHandle', render: (v: string) => <Text style={{ color: COLORS.primary }}>@{v}</Text> },
    { title: '显示名称', dataIndex: 'displayName', key: 'displayName', render: (v: string) => <Text style={{ color: 'rgba(0, 0, 0, 0.85)' }}>{v || '-'}</Text> },
    { title: '分类', dataIndex: 'category', key: 'category', render: (v: string) => <Tag style={{ background: `${COLORS.purple}22`, border: `1px solid ${COLORS.purple}44`, color: COLORS.purple }}>{v}</Tag> },
    { title: '话题', dataIndex: 'topics', key: 'topics', render: (v: string[]) => <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>{v?.slice(0, 3).join(', ') || '-'}</Text> },
    { title: '状态', dataIndex: 'enabled', key: 'enabled', render: (v: boolean) => <Badge status={v ? 'success' : 'default'} text={<Text style={{ color: v ? COLORS.green : '#6b7280' }}>{v ? '启用' : '禁用'}</Text>} /> },
  ];

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      <PageHeader icon={<SendOutlined />} title="内容分发" subtitle="管理内容池和分发任务" color={COLORS.orange} />

      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}><StatCard title="总任务数" value={stats.totalTasks || 0} color="rgba(0, 0, 0, 0.85)" /></Col>
          <Col span={6}><StatCard title="待处理" value={stats.pendingTasks || 0} color={COLORS.orange} /></Col>
          <Col span={6}><StatCard title="今日发布" value={stats.publishedToday || 0} color={COLORS.green} /></Col>
          <Col span={6}><StatCard title="今日活跃机器人" value={stats.activeBotsToday || 0} color={COLORS.cyan} /></Col>
        </Row>
      )}

      <SimpleCard accentColor={COLORS.orange}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
          { key: 'pool', label: <Space><FileTextOutlined style={{ color: COLORS.orange }} />内容池</Space>, children: (
            <Row gutter={24}>
              <Col xs={24} lg={12}>
                <Space style={{ marginBottom: 16 }} wrap>
                  <Select value={filterSource} onChange={setFilterSource} style={{ width: 140 }} allowClear placeholder="全部来源" options={[{ value: '', label: '全部来源' }, ...SOURCE_TYPE_OPTIONS.map(o => ({ value: o.code, label: o.label }))]} />
                  <Select value={filterStatus} onChange={setFilterStatus} style={{ width: 120 }} allowClear placeholder="全部状态" options={[{ value: '', label: '全部状态' }, ...STATUS_OPTIONS.map(o => ({ value: o.code, label: o.label }))]} />
                  <Button type="primary" icon={<SyncOutlined />} onClick={fetchTasks}>查询</Button>
                  <Button onClick={() => handleScan('blockbeats')} loading={scanning} style={{ background: COLORS.warning, borderColor: COLORS.warning, color: '#fff' }}>扫描BlockBeats</Button>
                  <Button onClick={() => handleScan('twitter')} loading={scanning} type="primary">扫描Twitter</Button>
                </Space>
                <List loading={loading} dataSource={tasks} locale={{ emptyText: <Empty description={<Text style={{ color: '#6b7280' }}>暂无内容</Text>} /> }} renderItem={task => (
                  <List.Item
                    onClick={() => setSelectedTask(task)}
                    style={{
                      cursor: 'pointer',
                      padding: 16,
                      marginBottom: 8,
                      background: selectedTask?.id === task.id ? `${COLORS.orange}15` : COLORS.darkBg,
                      borderRadius: 12,
                      border: selectedTask?.id === task.id ? `1px solid ${COLORS.orange}` : `1px solid ${COLORS.borderDark}`,
                      boxShadow: 'none',
                    }}
                    actions={[<Button size="small" type="primary" onClick={(e) => { e.stopPropagation(); handleAssign(task.id); }} loading={assigning} disabled={task.assignedCount >= task.maxAssign} style={{ background: COLORS.success, borderColor: COLORS.success }}>分配</Button>]}
                  >
                    <List.Item.Meta
                      avatar={task.sourceType === 'blockbeats' ? <FileTextOutlined style={{ fontSize: 24, color: COLORS.warning }} /> : <TwitterOutlined style={{ fontSize: 24, color: COLORS.primary }} />}
                      title={<Text style={{ color: 'rgba(0, 0, 0, 0.85)' }} ellipsis>{task.aiSummary?.substring(0, 50) || '(无摘要)'}...</Text>}
                      description={<Space>{getStatusTag(task.status)}<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>已分配: {task.assignedCount}/{task.maxAssign}</Text></Space>}
                    />
                  </List.Item>
                )} />
              </Col>
              <Col xs={24} lg={12}>
                <SimpleCard title={<Text style={{ color: 'rgba(0, 0, 0, 0.85)' }}>分发详情</Text>} style={{ background: COLORS.darkBg }} accentColor={COLORS.cyan}>
                  {selectedTask ? (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div><Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>来源: </Text><Text style={{ color: 'rgba(0, 0, 0, 0.85)' }}>{selectedTask.sourceType}</Text></div>
                      <div><Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>AI摘要: </Text><Text style={{ color: 'rgba(0, 0, 0, 0.85)' }}>{selectedTask.aiSummary || '(无)'}</Text></div>
                      <div><Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>关键词: </Text><Text style={{ color: 'rgba(0, 0, 0, 0.85)' }}>{selectedTask.keywords?.join(', ') || '(无)'}</Text></div>
                      <Text style={{ color: 'rgba(0, 0, 0, 0.45)', marginTop: 16 }}>已分配机器人:</Text>
                      <List loading={detailsLoading} dataSource={details} locale={{ emptyText: <Empty description={<Text style={{ color: '#6b7280' }}>暂无分配</Text>} /> }} renderItem={detail => (
                        <List.Item style={{ padding: 12, background: COLORS.cardBg, borderRadius: 8, marginBottom: 8, border: `1px solid ${COLORS.borderDark}` }} actions={detail.status === 'pending' ? [<Button type="primary" size="small" onClick={() => handlePublish(detail.id)} style={{ background: COLORS.success, borderColor: COLORS.success }}>发布</Button>] : []}>
                          <List.Item.Meta title={<Text style={{ color: 'rgba(0, 0, 0, 0.85)' }}>{detail.botName || `Bot#${detail.botId}`}</Text>} description={<><Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>{detail.transformedContent || '(待转化)'}</Text>{getStatusTag(detail.status)}</>} />
                        </List.Item>
                      )} />
                    </Space>
                  ) : <Empty description={<Text style={{ color: '#6b7280' }}>请选择内容查看详情</Text>} />}
                </SimpleCard>
              </Col>
            </Row>
          )},
          { key: 'twitter-author', label: <Space><TwitterOutlined style={{ color: COLORS.cyan }} />推特作者管理</Space>, children: (
            <Table columns={authorColumns} dataSource={authors} rowKey="id" loading={authorsLoading} pagination={{ pageSize: 10 }} />
          )},
        ]} />
      </SimpleCard>
    </div>
  );
};

export default ContentDistribution;
